import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Paper, 
  Typography, 
  Box, 
  Grid, 
  Chip,
  Stack,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { getQuestionnaireTemplate, getLatestQuestionnaireTemplate, CURRENT_QUESTIONNAIRE_VERSION } from './questionnaireTemplate';
import QuestionnaireVersionBadge from './QuestionnaireVersionBadge';
import FHIRQuestionnaire from './FHIRQuestionnaire';
import '../../../styles/FHIRQuestionnaireView.css';

const FHIRQuestionnaireView = ({ admissionId, onUpdate }) => {
  const [responses, setResponses] = useState({});
  const [status, setStatus] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [templateVersion, setTemplateVersion] = useState(null);
  const [currentTemplate, setCurrentTemplate] = useState(getLatestQuestionnaireTemplate());
  
  const sections = currentTemplate.item || [];

  useEffect(() => {
    loadQuestionnaire();
  }, [admissionId]);

  const loadQuestionnaire = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/questionnaire/${admissionId}`);
      if (data) {
        setResponses(data.questionnaire_response);
        setStatus(data.status);
        
        // Load the appropriate template version
        const storedVersion = data.template_version;
        setTemplateVersion(storedVersion);
        
        if (storedVersion) {
          // Use the stored version for existing questionnaires
          const versionedTemplate = getQuestionnaireTemplate(storedVersion);
          setCurrentTemplate(versionedTemplate);
        } else {
          // Fallback to latest template if no version stored
          const latestTemplate = getLatestQuestionnaireTemplate();
          setCurrentTemplate(latestTemplate);
        }
      }
    } catch (error) {
      console.error('Error loading questionnaire:', error);
    }
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    loadQuestionnaire();
    onUpdate?.();
  };

  const hasContent = (sectionData, section) => {
    if (!sectionData && !section) return false;
    
    // For repeating sections, check the array
    if (section?.repeats && Array.isArray(responses[section.linkId])) {
      return responses[section.linkId].some(item => 
        Object.values(item).some(value => 
          value && value !== '' && (!Array.isArray(value) || value.length > 0)
        )
      );
    }
    
    // For non-repeating sections, check individual fields in flat responses
    if (section?.item) {
      return section.item.some(item => {
        const value = responses[item.linkId];
        return value && value !== '' && (!Array.isArray(value) || value.length > 0);
      });
    }
    
    if (Array.isArray(sectionData)) {
      return sectionData.some(item => 
        Object.values(item).some(value => 
          value && value !== '' && (!Array.isArray(value) || value.length > 0)
        )
      );
    }
    
    if (typeof sectionData === 'object') {
      return Object.values(sectionData).some(value => 
        value && value !== '' && (!Array.isArray(value) || value.length > 0)
      );
    }
    
    return sectionData && sectionData !== '';
  };

  const formatValue = (value, item) => {
    if (!value) return null;
    
    if (Array.isArray(value)) {
      if (item?.answerOption) {
        return value.map(code => {
          const option = item.answerOption.find(opt => opt.valueCoding.code === code);
          return option?.valueCoding.display || code;
        }).join(', ');
      }
      return value.join(', ');
    }
    
    if (item?.answerOption) {
      const option = item.answerOption.find(opt => opt.valueCoding.code === value);
      return option?.valueCoding.display || value;
    }
    
    return value;
  };

  const renderCompactField = (item, value) => {
    const formattedValue = formatValue(value, item);
    if (!formattedValue) return null;

    return (
      <Box key={item.linkId} sx={{ mb: 1 }}>
        <Typography 
          variant="body1" 
          component="div" 
          sx={{ 
            fontWeight: 600, 
            color: 'primary.dark',
            mb: 0.3,
            fontSize: '0.95rem'
          }}
        >
          {item.text}
        </Typography>
        <Typography 
          variant="body1" 
          component="div" 
          sx={{ 
            pl: 1,
            color: 'text.primary',
            fontSize: '0.95rem',
            lineHeight: 1.4
          }}
        >
          {formattedValue}
        </Typography>
      </Box>
    );
  };

  const renderRepeatingSection = (section, sectionData) => {
    const repeatingData = responses[section.linkId];
    if (!Array.isArray(repeatingData) || !hasContent(null, section)) return null;

    return (
      <Card variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5 }}>
          <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', fontSize: '1.1rem' }}>
            {section.text}
          </Typography>
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  {section.item?.map(item => (
                    <TableCell 
                      key={item.linkId}
                      sx={{ 
                        fontWeight: 600, 
                        color: 'primary.dark',
                        fontSize: '0.9rem'
                      }}
                    >
                      {item.text}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {repeatingData.map((entry, index) => {
                  if (!hasContent(entry)) return null;
                  
                  return (
                    <TableRow 
                      key={index}
                      sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.25' } }}
                    >
                      {section.item?.map(item => {
                        const value = entry[item.linkId];
                        const formattedValue = formatValue(value, item);
                        
                        return (
                          <TableCell 
                            key={item.linkId}
                            sx={{ 
                              fontSize: '0.9rem',
                              color: 'text.primary',
                              verticalAlign: 'top'
                            }}
                          >
                            {formattedValue || '-'}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const renderSimpleSection = (section) => {
    if (!hasContent(null, section)) return null;

    return (
      <Card variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5 }}>
          <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', fontSize: '1.1rem' }}>
            {section.text}
          </Typography>
          
          {section.linkId === 'vitals' ? (
            <Grid container spacing={1}>
              {section.item?.map(item => {
                const value = responses[item.linkId];
                if (!value) return null;
                return (
                  <Grid item xs={6} sm={4} key={item.linkId}>
                    <Chip 
                      label={`${item.text}: ${value}`}
                      variant="outlined"
                      size="medium"
                      sx={{ fontSize: '0.85rem', width: '100%' }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Stack spacing={0.5}>
              {section.item?.map(item => {
                const value = responses[item.linkId];
                return renderCompactField(item, value);
              })}
            </Stack>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderSection = (section) => {
    if (section.type === 'group') {
      return section.repeats 
        ? renderRepeatingSection(section)
        : renderSimpleSection(section);
    }
    
    return null;
  };

  // Reorder sections to put Assessment & Plan first
  const reorderedSections = [...sections];
  const assessmentIndex = reorderedSections.findIndex(s => s.linkId === 'assessmentPlan');
  if (assessmentIndex > -1) {
    const assessmentSection = reorderedSections.splice(assessmentIndex, 1)[0];
    reorderedSections.unshift(assessmentSection);
  }

  const physicalExamSection = sections.find(s => s.linkId === 'physicalExam');
  const vitalsSection = physicalExamSection?.item?.find(i => i.linkId === 'vitals');

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1200, mx: 'auto' }} elevation={1}>
        <Box sx={{ mb: 3, borderBottom: '2px solid', borderColor: 'primary.main', pb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Patient Questionnaire Response
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'text.secondary', mt: 0.5 }}>
                History & Physical Examination Summary
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Version Badge */}
              <QuestionnaireVersionBadge version={templateVersion} />
              
              {status === 'completed' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.5rem' }} />
                  <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 600 }}>
                    Complete
                  </Typography>
                </Box>
              )}
              
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditDialogOpen(true)}
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.50'
                  }
                }}
              >
                Edit
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Administrative Details - Always show as header */}
        {hasContent(null, sections.find(s => s.linkId === 'administrative')) && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Grid container spacing={1.5}>
              {responses.patientName && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.dark', fontSize: '0.95rem' }}>
                    Patient: 
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '0.95rem' }}>
                    {responses.patientName}
                  </Typography>
                </Grid>
              )}
              {responses.visitDate && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.dark', fontSize: '0.95rem' }}>
                    Admission: 
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '0.95rem' }}>
                    {responses.visitDate}
                  </Typography>
                </Grid>
              )}
              {responses.attendingPhysician && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.dark', fontSize: '0.95rem' }}>
                    Attending: 
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '0.95rem' }}>
                    {responses.attendingPhysician}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Vital Signs - Special treatment */}
        {vitalsSection && vitalsSection.item?.some(item => responses[item.linkId]) && (
          <Card variant="outlined" sx={{ mb: 1.5, bgcolor: 'warning.50' }}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="h6" sx={{ mb: 1.5, color: 'warning.dark', fontSize: '1.1rem' }}>
                Vital Signs
              </Typography>
              <Grid container spacing={1}>
                {vitalsSection.item?.map(item => {
                  const value = responses[item.linkId];
                  if (!value) return null;
                  return (
                    <Grid item xs={6} sm={4} md={2.4} key={item.linkId}>
                      <Chip 
                        label={`${item.text.replace('Rate', '').replace('Pressure', 'BP')}: ${value}`}
                        variant="filled"
                        size="medium"
                        sx={{ 
                          fontSize: '0.85rem',
                          bgcolor: 'warning.100',
                          color: 'warning.dark',
                          width: '100%'
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* All other sections in reordered manner */}
        {reorderedSections.map(section => {
          // Skip administrative (already shown), vitals (already shown), and physicalExam (handle separately)
          if (['administrative', 'physicalExam'].includes(section.linkId)) {
            if (section.linkId === 'physicalExam') {
              // Render physical exam sections except vitals
              const examItems = section.item?.filter(item => item.linkId !== 'vitals');
              const hasExamData = examItems?.some(item => responses[item.linkId]);
              
              if (!hasExamData) return null;
              
              return (
                <Card variant="outlined" sx={{ mb: 1.5 }} key={section.linkId}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', fontSize: '1.1rem' }}>
                      Physical Examination
                    </Typography>
                    <Stack spacing={0.5}>
                      {examItems?.map(item => {
                        const value = responses[item.linkId];
                        return renderCompactField(item, value);
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              );
            }
            return null;
          }
          
          return renderSection(section);
        })}
      </Paper>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="h6">Edit Questionnaire</Typography>
          <IconButton onClick={handleEditClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
            <FHIRQuestionnaire
              admissionId={admissionId}
              onClose={handleEditClose}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FHIRQuestionnaireView;
